import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import ExactLocation from './ExactLocation';
import IncidentTypeService from '../../services/IncidentTypeService';
import AnnouncementService from '../../services/AnnouncementService';
import Select from '../ui/Select';
import TagInput from '../ui/TagInput';
import Button from '../ui/Button';

const cbrneHazards = [
    // Chemical Hazards
    "Ammonia",
    "Chlorine",
    "Sarin",
    "Mustard gas",
    "Phosgene",
    "VX (nerve agent)",
    "Hydrogen cyanide",
    "Carbon monoxide",
    "Sulfur dioxide",
    "Nitrogen dioxide",
    "Benzene",
    "Formaldehyde",
    "Toluene",
    "Ethylene oxide",
    "Pesticides",
    "Mercury",
    "Lead",
    "Arsenic",
    "Cyanogen chloride",
    "Hydrogen sulfide",

    // Biological Hazards
    "Anthrax",
    "Smallpox",
    "Plague",
    "Botulism",
    "Tularemia",
    "Viral hemorrhagic fevers (e.g., Ebola)",
    "Ricin",
    "Brucellosis",
    "Q fever",
    "Hantavirus",
    "Dengue fever",
    "Zika virus",
    "Cholera",
    "Tuberculosis",
    "Influenza (pandemic strains)",
    "SARS",
    "MERS",
    "HIV/AIDS",
    "Malaria",
    "West Nile virus",

    // Radiological Hazards
    "Cesium-137",
    "Cobalt-60",
    "Iodine-131",
    "Strontium-90",
    "Plutonium-239",
    "Uranium-235",
    "Radium-226",
    "Americium-241",
    "Polonium-210",
    "Radon",
    "Technetium-99m",
    "Tritium",
    "Thorium-232",
    "Curium-244",
    "Neptunium-237",
    "Promethium-147",
    "Yttrium-90",
    "Iridium-192",
    "Carbon-14",
    "Phosphorus-32",

    // Nuclear Hazards
    "Nuclear reactor meltdown",
    "Improvised nuclear device (IND)",
    "Dirty bomb (radiological dispersal device)",
    "Nuclear weapon detonation",
    "Nuclear fallout",
    "Criticality accident",
    "Plutonium exposure",
    "Neutron radiation",
    "Alpha radiation exposure",
    "Beta radiation exposure",
    "Gamma radiation exposure",
    "Spent nuclear fuel",
    "Enriched uranium",
    "Depleted uranium",
    "Reactor coolant leak",
    "Control rod failure",
    "Nuclear waste transportation accident",
    "Reprocessing plant accident",
    "Weapons-grade fissile material",
    "Fusion energy accident",

    // Explosive Hazards
    "TNT (trinitrotoluene)",
    "C-4 (composition C-4)",
    "RDX (Research Department Explosive)",
    "PETN (Pentaerythritol tetranitrate)",
    "ANFO (Ammonium nitrate/fuel oil)",
    "Dynamite",
    "Nitroglycerin",
    "Black powder",
    "TATP (Triacetone triperoxide)",
    "HMX (High Melting Explosive)",
    "Semtex",
    "Plastic explosives",
    "Landmines",
    "Improvised explosive devices (IEDs)",
    "Pipe bombs",
    "Car bombs",
    "Suicide vests",
    "Grenades",
    "Rocket-propelled grenades (RPGs)",
    "Mortar shells"
];


const MethaneForm = ({ simulate = false }) => {

    const navigate = useNavigate();

    // Incident Types
    const [incidentTypes, setIncidentTypes] = useState([]);
    const loadIncidentTypes = async () => {
        try {
            let response = await IncidentTypeService.findAll(1, 10);
            if (response && response.status === 200) {
                setIncidentTypes(response.data);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        loadIncidentTypes();
    }, []);

    const [formData, setFormData] = useState({
        title: "",
        description: "",
        isMajor: false,
        location: "",
        landmark: "",
        incidentTypeId: '',
        hazards: [],
        access: [],
        lowCasualities: [],
        highCasualities: [],
        mediumCasualities: [],
        erServicePresent: [],
        erServiceRequired: [],
        drill: simulate,

    });


    const handleSubmit = async (event) => {
        event.preventDefault();
        const hazards = formData.hazards.join(",");
        const access = formData.access.join(",");
        const lowCasualities = formData.lowCasualities.join(",");
        const mediumCasualities = formData.mediumCasualities.join(",");
        const highCasualities = formData.highCasualities.join(",");
        const erServicePresent = formData.erServicePresent.join(",");
        const erServiceRequired = formData.erServiceRequired.join(",");

        const body = {
            ...formData,
            hazards, access, lowCasualities, mediumCasualities, highCasualities, erServicePresent, erServiceRequired
        }

        const response = await AnnouncementService.create(body);
        if (!response.data.error) {
            const created = response.data.data;
            // Created as Pending — continue to the event page to assign teams and activate
            if (created?.id) navigate(`/active-incident/${created.id}`);
            else navigate(simulate ? "/activation/drills" : "/activation");
        }
    };

    const handleMajor = (event) => {
        setFormData({ ...formData, isMajor: event.target.checked });
    };

    return (
        <div className="max-w-5xl">
            <h1 className="text-xl font-bold text-neutral-900 mb-1">{simulate ? "Drill Activation" : "Activation"}</h1>
            <p className="text-sm text-neutral-500 mb-6">
                {simulate
                    ? "Create a drill using the METHANE framework."
                    : "Create a real incident announcement using the METHANE framework."}
            </p>
            <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-neutral-200 shadow-subtle p-6 space-y-6">
                <label className="flex items-center gap-3 cursor-pointer w-fit">
                    <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={formData.isMajor}
                        onChange={handleMajor}
                    />
                    <span className="w-11 h-6 bg-neutral-200 peer-checked:bg-seha-500 rounded-full transition-colors relative inline-block shrink-0">
                        <span className="absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full shadow transition-transform peer-checked:translate-x-5" />
                    </span>
                    <span className="text-sm font-semibold text-neutral-700">M - Major Incident</span>
                </label>

                <div>
                    <label className="form-label">Title</label>
                    <input
                        className="form-input"
                        required
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <ExactLocation formData={formData} setFormData={setFormData} />
                    <div>
                        <label className="form-label">E - Landmark</label>
                        <input
                            className="form-input"
                            value={formData.landmark}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                }
                            }}
                            onChange={(event) => {
                                setFormData({ ...formData, landmark: event.target.value });
                            }}
                        />
                    </div>
                </div>

                <Select
                    label="T - Type Of Incident"
                    name="incidentType"
                    value={formData.incidentTypeId}
                    onChange={({ target }) => setFormData(({ ...formData, incidentTypeId: target.value }))}
                >
                    <option value="" disabled>Select incident type</option>
                    {incidentTypes && incidentTypes?.map((option) => (
                        <option key={option.id} value={option.id}>
                            {option.incident_type_name}
                        </option>
                    ))}
                </Select>

                <TagInput
                    label="H - Potential Hazards"
                    value={formData.hazards}
                    onChange={(newValue) => setFormData({ ...formData, hazards: newValue })}
                    options={cbrneHazards}
                    placeholder="Add hazard..."
                />

                <TagInput
                    label="A - Access"
                    value={formData.access}
                    onChange={(newValue) => setFormData({ ...formData, access: newValue })}
                    options={[]}
                    placeholder="Add access point..."
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <TagInput
                        label="N - Low Casualties"
                        value={formData.lowCasualities}
                        onChange={(newValue) => setFormData({ ...formData, lowCasualities: newValue })}
                        options={[]}
                    />
                    <TagInput
                        label="N - Medium Casualties"
                        value={formData.mediumCasualities}
                        onChange={(newValue) => setFormData({ ...formData, mediumCasualities: newValue })}
                        options={[]}
                    />
                    <TagInput
                        label="N - High Casualties"
                        value={formData.highCasualities}
                        onChange={(newValue) => setFormData({ ...formData, highCasualities: newValue })}
                        options={[]}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <TagInput
                        label="E - Emergency Services Present"
                        value={formData.erServicePresent}
                        onChange={(newValue) => setFormData({ ...formData, erServicePresent: newValue })}
                        options={[]}
                    />
                    <TagInput
                        label="E - Emergency Services Required"
                        value={formData.erServiceRequired}
                        onChange={(newValue) => setFormData({ ...formData, erServiceRequired: newValue })}
                        options={[]}
                    />
                </div>

                <div>
                    <label className="form-label">Description</label>
                    <textarea
                        className="form-input"
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                </div>

                <div className="pt-2">
                    <Button type="submit" variant="primary">
                        Submit
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default MethaneForm;
